import { motion } from "framer-motion";
import {
  Users, UserPlus, RefreshCcw, ShieldCheck, HeartHandshake,
  Code2, Boxes, Smartphone, Workflow, LifeBuoy, ArrowRight,
} from "lucide-react";

const HR_IMG = "https://images.pexels.com/photos/7581125/pexels-photo-7581125.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IT_IMG = "https://images.unsplash.com/photo-1748256467077-c75ef01579aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBIUiUyMHRlYW0lMjBzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwb2ZmaWNlfGVufDB8fHx8MTc5MDI1NTE4Mnww&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  {
    id: "hr",
    title: "Human Resource Services",
    tagline: "The right people, at the right time.",
    image: HR_IMG,
    imageAlt: "HR professionals in a recruitment meeting",
    items: [
      { icon: Users, name: "Talent Recruitment & Staffing" },
      { icon: UserPlus, name: "Temporary & Contractual Hiring" },
      { icon: RefreshCcw, name: "Recruitment Process Outsourcing (RPO)" },
      { icon: ShieldCheck, name: "HR Strategy & Compliance Advisory" },
      { icon: HeartHandshake, name: "Culture Building & Engagement" },
    ],
  },
  {
    id: "it",
    title: "Software & IT Services",
    tagline: "Technology that scales with you.",
    image: IT_IMG,
    imageAlt: "Software engineers building business applications",
    items: [
      { icon: Code2, name: "Custom Software Development" },
      { icon: Boxes, name: "ERP Implementation (Frappe/ERPNext)" },
      { icon: Smartphone, name: "Mobile & Web App Development" },
      { icon: Workflow, name: "Business Automation Solutions" },
      { icon: LifeBuoy, name: "IT Consulting & Support" },
    ],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-20 lg:py-28 relative grid-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F89335]">What We Do</p>
          <h2 data-testid="services-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Two Pillars. <span className="text-gradient-gold">One Partner.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
            From building high-performing teams to building the software that powers them — Krijo delivers both sides of business growth.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8">
          {CATEGORIES.map((cat, ci) => (
            <motion.div
              key={cat.id}
              data-testid={`services-card-${cat.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: ci * 0.12 }}
              className="glass-card rounded-3xl overflow-hidden group"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.imageAlt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#010433] via-[#010433]/40 to-transparent" />
                <div className="absolute bottom-4 left-6 right-6">
                  <h3 className="font-['Poppins'] text-xl sm:text-2xl font-semibold">{cat.title}</h3>
                  <p className="text-sm text-[#F9AE46] mt-1">{cat.tagline}</p>
                </div>
              </div>

              <ul className="p-6 sm:p-8 space-y-1">
                {cat.items.map((item) => (
                  <li
                    key={item.name}
                    data-testid={`service-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
                    className="flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-white/5 transition-colors duration-200"
                  >
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-[#F89335]/12 flex items-center justify-center">
                      <item.icon size={19} className="text-[#F89335]" />
                    </span>
                    <span className="text-sm sm:text-base text-slate-200 font-medium">{item.name}</span>
                  </li>
                ))}
              </ul>

              <div className="px-6 sm:px-8 pb-7">
                <a
                  href="#contact"
                  data-testid={`services-cta-${cat.id}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#F89335] hover:text-[#F9AE46] transition-colors"
                >
                  Discuss your requirement <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
